// Matomo Event Tracking Utilities
// Works with the HTML-based Matomo tracking code

/**
 * Track a custom event in Matomo
 */
export const trackMatomoEvent = (category: string, action: string, name?: string, value?: number) => {
  if (typeof window !== 'undefined' && (window as any)._paq) {
    (window as any)._paq.push(['trackEvent', category, action, name, value]);
    console.log('📊 Matomo event tracked:', { category, action, name, value });
  }
};

/**
 * Track course registration
 */
export const trackCourseRegistration = (courseTitle: string, courseId: string, participantCount: number) => {
  trackMatomoEvent('Course Registration', 'Registration Submitted', courseTitle, participantCount);
  trackMatomoEvent('Conversion', 'Course Registration', courseId, participantCount);
};

/**
 * Track contact form submission
 */
export const trackContactForm = (subject: string) => {
  trackMatomoEvent('Contact', 'Form Submitted', subject);
};

/**
 * Track admin actions
 */
export const trackAdminAction = (action: string, details?: string) => {
  trackMatomoEvent('Admin', action, details);
};

/**
 * Track page view (for SPA navigation)
 */
export const trackPageView = (url: string, title: string) => {
  if (typeof window !== 'undefined' && (window as any)._paq) {
    (window as any)._paq.push(['setCustomUrl', url]);
    (window as any)._paq.push(['setDocumentTitle', title]);
    (window as any)._paq.push(['trackPageView']);
    console.log('📊 Matomo page view tracked:', { url, title });
  }
};

/**
 * Interface for error context (GDPR-safe, no PII)
 */
interface ErrorContext {
  formName: string;
  errorType?: string;
  step?: string | number;
  courseId?: string;
  participantCount?: number;
  registrationType?: string;
  validationField?: string;
  timestamp?: string;
}

/**
 * Track form errors in Matomo (GDPR-compliant - no PII)
 * @param errorMessage - Error message (sanitized, no PII)
 * @param context - Non-PII context about the error
 */
export const trackFormError = (errorMessage: string, context: ErrorContext) => {
  // Ensure error message doesn't contain PII
  const sanitizedMessage = errorMessage
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[email]') // Remove emails
    .replace(/\b\d{10,}\b/g, '[phone]') // Remove phone numbers
    .replace(/\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, '[name]'); // Remove names pattern

  // Build event name with context
  const eventName = `${context.formName}${context.step ? ` - Step ${context.step}` : ''}`;
  
  // Track error event
  trackMatomoEvent(
    'Form Error',
    context.errorType || 'Submission Error',
    eventName,
    context.participantCount
  );

  // Track additional context as separate event for detailed analysis
  const contextDetails = {
    formName: context.formName,
    errorType: context.errorType,
    step: context.step,
    ...(context.courseId && { courseId: context.courseId }),
    ...(context.participantCount && { participantCount: context.participantCount }),
    ...(context.registrationType && { registrationType: context.registrationType }),
    ...(context.validationField && { validationField: context.validationField }),
    timestamp: context.timestamp || new Date().toISOString()
  };

  console.error('📊 Form error tracked:', {
    message: sanitizedMessage,
    context: contextDetails
  });

  // Track as content interaction for funnel analysis
  if (typeof window !== 'undefined' && (window as any)._paq) {
    (window as any)._paq.push([
      'trackContentInteraction',
      'error',
      context.formName,
      sanitizedMessage
    ]);
  }
};

/**
 * Track form validation errors
 * @param formName - Name of the form
 * @param field - Field that failed validation (e.g., 'email', 'phone')
 * @param validationError - Type of validation error
 */
export const trackValidationError = (
  formName: string,
  field: string,
  validationError: string
) => {
  trackFormError(validationError, {
    formName,
    errorType: 'Validation Error',
    validationField: field
  });
};

/**
 * Track rate limiting/spam detection
 * @param formName - Name of the form
 * @param reason - Reason for rate limit (e.g., 'Too fast', 'Too many requests')
 */
export const trackSpamDetection = (formName: string, reason: string) => {
  trackMatomoEvent('Security', 'Spam Detection', `${formName} - ${reason}`);
  console.log('🛡️ Spam detection tracked:', { formName, reason });
};

/**
 * Track form abandonment (user left form without submitting)
 * @param formName - Name of the form
 * @param step - Step where user abandoned
 * @param fieldsCompleted - Number of fields completed
 */
export const trackFormAbandonment = (
  formName: string,
  step: string | number,
  fieldsCompleted: number
) => {
  trackMatomoEvent(
    'Form Abandonment',
    formName,
    `Step ${step}`,
    fieldsCompleted
  );
};

/**
 * Track form step progression
 * @param formName - Name of the form
 * @param step - Current step number
 * @param stepName - Name/description of the step
 * @param action - Action taken ('entered', 'completed', 'back')
 */
export const trackFormStep = (
  formName: string,
  step: number,
  stepName: string,
  action: 'entered' | 'completed' | 'back'
) => {
  trackMatomoEvent(
    'Form Progress',
    `${formName} - ${action}`,
    `Step ${step}: ${stepName}`,
    step
  );
  console.log('📊 Form step tracked:', { formName, step, stepName, action });
};

/**
 * Track button clicks in forms
 * @param formName - Name of the form
 * @param buttonName - Name/label of the button
 * @param step - Current step (optional)
 */
export const trackFormButton = (
  formName: string,
  buttonName: string,
  step?: number
) => {
  trackMatomoEvent(
    'Form Interaction',
    'Button Click',
    `${formName} - ${buttonName}${step ? ` (Step ${step})` : ''}`,
    step
  );
  console.log('🖱️ Button click tracked:', { formName, buttonName, step });
};

/**
 * Track time spent on a form step
 * @param formName - Name of the form
 * @param step - Step number
 * @param timeSpentSeconds - Time spent in seconds
 */
export const trackStepTime = (
  formName: string,
  step: number,
  timeSpentSeconds: number
) => {
  trackMatomoEvent(
    'Form Timing',
    formName,
    `Step ${step} - ${timeSpentSeconds}s`,
    timeSpentSeconds
  );
  console.log('⏱️ Step time tracked:', { formName, step, timeSpentSeconds });
};
